import { useState, useEffect, useCallback } from "react";
import { Loader2, UserPlus, Trash2, KeyRound, Users, Package } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";

const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return iso; } };

export default function PartnersAdmin() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", password: "" });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/partners"); setPartners(data); }
    catch { toast.error("Failed to load partners"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/admin/partners", form);
      toast.success(`Partner account created for ${form.company_name}`);
      setForm({ company_name: "", contact_name: "", email: "", password: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Create failed"); }
    finally { setCreating(false); }
  };

  const resetPw = async (p) => {
    const pw = window.prompt(`New password for ${p.company_name}:`);
    if (!pw) return;
    try { await api.post(`/admin/partners/${p.id}/reset-password`, { password: pw }); toast.success("Password reset"); }
    catch { toast.error("Reset failed"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove partner ${p.company_name}? They will lose portal access.`)) return;
    try { await api.delete(`/admin/partners/${p.id}`); toast.success("Partner removed"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div data-testid="partners-admin">
      <div className="flex items-center gap-2 mb-6"><Users className="h-5 w-5 text-flame" /><h2 className="font-display font-bold text-xl text-white">Partner Accounts</h2></div>

      <form onSubmit={create} className="bg-ink border border-white/10 p-6 mb-8" data-testid="partner-create-form">
        <div className="flex items-center gap-2 text-flame mb-4"><UserPlus className="h-4 w-4" /><span className="kicker">New Partner</span></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <input data-testid="partner-company" required placeholder="Company name *" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="bg-charcoal border border-white/15 px-3 py-2.5 text-white text-sm focus:border-flame focus:outline-none" />
          <input data-testid="partner-contact" placeholder="Contact name" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="bg-charcoal border border-white/15 px-3 py-2.5 text-white text-sm focus:border-flame focus:outline-none" />
          <input data-testid="partner-email" required type="email" placeholder="Login email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-charcoal border border-white/15 px-3 py-2.5 text-white text-sm focus:border-flame focus:outline-none" />
          <input data-testid="partner-password" required placeholder="Starter password *" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-charcoal border border-white/15 px-3 py-2.5 text-white text-sm focus:border-flame focus:outline-none" />
        </div>
        <div className="mt-4 flex items-center justify-between">
          <p className="text-xs text-white/40">They log in at <span className="text-flame">/partnerportal</span> with these credentials.</p>
          <button data-testid="partner-create-btn" disabled={creating} className="inline-flex items-center gap-2 bg-flame text-white px-5 py-2.5 text-sm font-semibold hover:bg-flame/90 transition-colors disabled:opacity-50">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Create Partner
          </button>
        </div>
      </form>

      {loading ? <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-flame" /></div> : (
        <div className="bg-ink border border-white/10" data-testid="partners-list">
          {partners.length === 0 && <div className="p-8 text-center text-white/40">No partner accounts yet. Create one above.</div>}
          {partners.map((p) => (
            <div key={p.id} className="flex items-center justify-between p-4 border-b border-white/5 last:border-0" data-testid={`partner-row-${p.id}`}>
              <div>
                <div className="font-semibold text-white">{p.company_name}</div>
                <div className="text-xs text-white/50">{p.name ? `${p.name} · ` : ""}{p.email} · added {fmt(p.created_at)}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => resetPw(p)} data-testid={`partner-reset-${p.id}`} className="inline-flex items-center gap-1.5 text-xs border border-white/15 px-3 py-1.5 hover:border-flame hover:text-flame transition-colors"><KeyRound className="h-3.5 w-3.5" /> Reset PW</button>
                <button onClick={() => remove(p)} data-testid={`partner-delete-${p.id}`} className="p-2 text-white/40 hover:text-red-400 transition-colors"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
